import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loadable, Tabbar, TabPanel, useTabs, useLoad, fmt, ActionButton } from "@/components/control/kit";
import { V3Page, V3Aside, CheckCard, Field, Notice } from "@/components/control/v3";

// C111–C115: recorded test results only. A PASS is never derived from a picture or claimed without evidence.
const KINDS = {
  two_org: { tab: "Zwei Organisationen", primary: "Prüfergebnis erfassen", subtitle: "Halte den Test zwischen zwei getrennten Organisationen nachvollziehbar fest." },
  a11y: { tab: "Barrierefreiheit", primary: "Testergebnis nachführen", subtitle: "Halte Tastatur-, Screenreader-, Zoom- und Kontrastprüfungen mit Nachweis fest." },
  restore: { tab: "Wiederherstellung", primary: "Testprotokoll erfassen", subtitle: "Eine Wiederherstellung wird in einer getrennten TEST-Umgebung nachgewiesen." },
  test_release: { tab: "TEST-Freigabe", primary: "TEST-Prüfung vorlegen", subtitle: "Lege Nachweise vor, bevor ein DEV-Stand nach TEST übernommen wird." },
  prod_release: { tab: "PROD-Freigabe", primary: "PROD-Freigabe prüfen", subtitle: "Prüfe Nachweise, bevor ein TEST-Stand in PROD veröffentlicht wird." },
};

const recordFields = (kind, items) => [
  { name: "item", label: "Prüfpunkt", type: "select", required: true, options: items.map((i) => ({ value: i.item, label: i.label })) },
  { name: "result", label: "Ergebnis", type: "select", required: true, options: [{ value: "bestanden", label: "Bestanden" }, { value: "nicht_bestanden", label: "Nicht bestanden" }] },
  { name: "revision", label: kind === "restore" ? "Sicherungsstand" : "Revision (Commit)", required: kind !== "a11y" },
  { name: "environment_url", label: kind === "restore" ? "Ziel (getrennte TEST-Umgebung, https://…)" : "Umgebungsadresse (https://…)" },
  { name: "method", label: kind === "restore" ? "Verantwortlich" : "Methode / Werkzeug" },
  { name: "evidence", label: kind === "restore" ? "Testprotokoll" : "Nachweis (Ablauf, Ergebnis, Beleg)", type: "textarea", required: true, help: "Mindestens 20 Zeichen. Keine Zugangsdaten oder Geheimnisse eintragen." },
];

function KindView({ k, kind }) {
  const latest = k.items.map((i) => i.record).filter(Boolean).sort((a, b) => (a.recorded_at < b.recorded_at ? 1 : -1))[0];
  return (
    <div className="space-y-2.5" data-testid={`qa-kind-${kind}`}>
      {k.items.map((i) => (
        <CheckCard key={i.item} testId={`qa-item-${kind}-${i.item}`} title={i.label} status={i.status}
          hint={i.record ? `${fmt(i.record.recorded_at)} · ${i.record.recorded_by?.name}${i.record.revision ? ` · ${i.record.revision}` : ""} · ${i.record.evidence}` : i.hint || "Ausstehend"} />
      ))}
      <Field label="Testergebnis" testId={`qa-result-${kind}`}>
        {latest ? `Zuletzt erfasst ${fmt(latest.recorded_at)} · ${latest.result === "bestanden" ? "bestanden" : "nicht bestanden"}` : "Ausstehend · Kein PASS aus einem Bild ableiten"}
      </Field>
    </div>
  );
}

export default function QualityChecks() {
  const { kind = "two_org" } = useParams();
  const navigate = useNavigate();
  const st = useLoad("/qa-checks");
  const tabs = useTabs(Object.entries(KINDS).map(([value, c]) => ({ value, label: c.tab })), kind);
  const conf = KINDS[tabs.value];
  const k = st.data?.kinds.find((x) => x.kind === tabs.value);
  return (
    <V3Page crumb="Systemstatus" title={k?.title || "Prüfungen"} subtitle={conf.subtitle} testId="qa-page"
      aside={<V3Aside subject={k?.items[0]?.hint || conf.subtitle} />}>
      <Tabbar tabs={tabs} onChange={(v) => navigate(`/qualitaet/${v}`)} testId="qa-tabs" ariaLabel="Prüfbereiche" />
      <TabPanel tabs={tabs} testId="qa-panel">
        <Loadable state={st}>
          {(d) => (
            <div className="space-y-3">
              <KindView k={d.kinds.find((x) => x.kind === tabs.value)} kind={tabs.value} />
              <Notice title="Nur echte Ergebnisse" text={d.note} testId="qa-note" />
              <div className="flex flex-col gap-3 sm:flex-row">
                <ActionButton label={conf.primary} variant="default" testId={`qa-record-${tabs.value}`} submitLabel="Ergebnis speichern" onDone={st.reload}
                  className="h-11 rounded-lg px-6 text-base font-bold sm:min-w-[17rem]" title={conf.primary}
                  fields={recordFields(tabs.value, d.kinds.find((x) => x.kind === tabs.value).items)}
                  onSubmit={async (v) => { await api.post("/qa-checks", { ...v, kind: tabs.value }); return "Prüfergebnis gespeichert."; }} />
                <button type="button" onClick={() => navigate("/systemstatus")} data-testid="qa-back"
                  className="h-11 rounded-lg border border-[#E6E1F0] bg-white px-6 text-base font-bold text-brand-purple sm:min-w-[10rem]">Zurück</button>
              </div>
            </div>
          )}
        </Loadable>
      </TabPanel>
    </V3Page>
  );
}
